import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { warningTextTone } from '../utils/warningTone';

const LABEL_SHAPE = PropTypes.shape({
  ndc: PropTypes.string,
  lot: PropTypes.string,
  note: PropTypes.string,
  claimsDeficit: PropTypes.bool,
});

/** One side of the strip. Each value is one text run, and a changed one reads green. */
const LabelSide = ({ title, label, changedNdc, changedLot }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant='caption'
      color='text.secondary'
      component='div'
      sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.65rem' }}
    >
      {title}
    </Typography>
    <Typography
      variant='caption'
      component='div'
      sx={{
        color: changedNdc ? 'success.main' : 'text.primary',
        fontWeight: changedNdc ? 600 : 400,
      }}
    >
      {`NDC ${label.ndc}`}
    </Typography>
    <Typography
      variant='caption'
      component='div'
      sx={{
        color: changedLot ? 'success.main' : 'text.primary',
        fontWeight: changedLot ? 600 : 400,
      }}
    >
      {`Lot ${label.lot}`}
    </Typography>
    {label.note && (
      <Typography
        variant='caption'
        component='div'
        sx={{ color: label.claimsDeficit ? warningTextTone : 'text.secondary' }}
      >
        {label.note}
      </Typography>
    )}
  </Box>
);

LabelSide.propTypes = {
  title: PropTypes.string.isRequired,
  label: LABEL_SHAPE.isRequired,
  changedNdc: PropTypes.bool,
  changedLot: PropTypes.bool,
};

LabelSide.defaultProps = { changedNdc: false, changedLot: false };

/**
 * POM-6534 — what the label prints now, and what it will print after Apply. Rendered only while
 * Apply is enabled, so it always describes something Apply would actually do.
 */
const ChangeNdcLotBeforeAfter = ({ before, after }) => (
  <Box
    data-testid='change-ndc-lot-before-after'
    sx={{
      flex: '1 1 360px',
      minWidth: 0,
      mr: 1,
      px: 1.5,
      py: 0.75,
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
      columnGap: 1.5,
      alignItems: 'center',
      borderRadius: 1,
      border: '1px solid',
      borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
      bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
    }}
  >
    <LabelSide title='Before' label={before} />
    <Typography color='text.secondary' aria-hidden>
      →
    </Typography>
    <LabelSide
      title='After'
      label={after}
      changedNdc={after.ndc !== before.ndc}
      changedLot={after.lot !== before.lot}
    />
  </Box>
);

ChangeNdcLotBeforeAfter.propTypes = {
  before: LABEL_SHAPE.isRequired,
  after: LABEL_SHAPE.isRequired,
};

export default ChangeNdcLotBeforeAfter;
